import { Injectable } from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash-es';

import { CompanyBilling } from '@site-mate/dashpivot-shared-library';
import {
  AccountTypes,
  BillingCycles,
  CompanyPlanTypes,
  IAccount,
  IBillingPayment,
  IBillingSubscription,
  PaymentCurrencyTypes,
  PaymentMethods,
  PaymentTermContexts,
  SubscriptionStatuses,
  SubscriptionTerms,
} from '@site-mate/sitemate-global-shared';

import { ICompanyHomeSettings } from 'app/home/company-home-settings.model';

export interface IGlobalBillingSettingsForm {
  billingCompany?: string;
  billingAddress?: string;
  billingContact?: string;
  billingDate?: NgbDate;
  initialSubscriptionDate?: NgbDate;
  paymentTermDay?: number;
  paymentTermContext?: PaymentTermContexts;
  paymentMethod?: PaymentMethods;
  paymentCurrency?: PaymentCurrencyTypes;
  accountType?: AccountTypes;
  automatedBilling?: boolean;
}

export interface IDashpivotBillingSettingsForm {
  dashpivotTrialEndDate?: NgbDate;
  dashpivotPlanType?: CompanyPlanTypes;
  dashpivotSubscriptionStatus?: SubscriptionStatuses;
  dashpivotInitialSubscriptionDate?: NgbDate;
  dashpivotSubscriptionTerm?: SubscriptionTerms;
  dashpivotBillingCycle?: BillingCycles;
  groupBilling?: boolean;
  dashpivotLegacyPlanRestrictionsEnabled?: boolean;
  contributorUsersPaid?: number;
  sitematePaidUsersPaid?: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkspaceBillingService {
  constructor() {}

  buildGlobalBillingSettingsForm(account: IAccount): IGlobalBillingSettingsForm {
    const hasBillingDate = Boolean(account?.billing?.billingDate);
    const billingDate = hasBillingDate ? this.convertToNgbDate(account?.billing?.billingDate) : null;

    const hasInitialSubscriptionDate = Boolean(account?.billing?.initialSubscriptionDate);
    const initialSubscriptionDate = hasInitialSubscriptionDate
      ? this.convertToNgbDate(account?.billing?.initialSubscriptionDate)
      : null;

    const form: IGlobalBillingSettingsForm = {
      billingCompany: account?.billing?.billingCompanyName,
      billingAddress: account?.billing?.billingAddress,
      billingContact: account?.billing?.billingContact,
      billingDate,
      initialSubscriptionDate,
      paymentTermDay: account?.billing?.paymentTermDay,
      paymentTermContext: account?.billing?.paymentTermContext,
      paymentMethod: account?.billing?.paymentMethod,
      paymentCurrency: account?.billing?.currency,
      accountType: account?.accountType,
      automatedBilling: account?.automatedBilling,
    };

    return cloneDeep(form);
  }

  buildDashpivotBillingSettingsForm(
    account: IAccount,
    legacySettings: ICompanyHomeSettings,
  ): IDashpivotBillingSettingsForm {
    const hasDashpivotTrialEndDate = Boolean(account?.billing?.dashpivot?.trialEndDate);
    const dashpivotTrialEndDate = hasDashpivotTrialEndDate
      ? this.convertToNgbDate(account?.billing?.dashpivot?.trialEndDate)
      : null;

    const hasDashpivotInitialSubscriptionDate = Boolean(account?.billing?.dashpivot?.initialSubscriptionDate);
    const dashpivotInitialSubscriptionDate = hasDashpivotInitialSubscriptionDate
      ? this.convertToNgbDate(account?.billing?.dashpivot?.initialSubscriptionDate)
      : null;

    const form: IDashpivotBillingSettingsForm = {
      dashpivotTrialEndDate,
      dashpivotPlanType: account?.billing?.dashpivot?.planType,
      dashpivotSubscriptionStatus: account?.billing?.dashpivot?.subscriptionStatus,
      dashpivotInitialSubscriptionDate,
      dashpivotSubscriptionTerm: account?.billing?.dashpivot?.subscriptionTerm,
      dashpivotBillingCycle: account?.billing?.dashpivot?.billingCycle,
      dashpivotLegacyPlanRestrictionsEnabled: account?.billing?.dashpivot?.legacyPlanRestrictionsEnabled,
      contributorUsersPaid: account?.billing?.dashpivot?.contributorUsersPaid,
      sitematePaidUsersPaid: account?.billing?.dashpivot?.sitematePaidUsersPaid,

      // DP DB Settings Only
      groupBilling: legacySettings.groupBilling,
    };

    return cloneDeep(form);
  }

  buildLegacyCompanyBilling(settings: ICompanyHomeSettings): CompanyBilling {
    const trialExpiration = this.convertToUTC(settings.trialExpiration);
    const billing = {} as CompanyBilling;

    billing.planType = settings.planType;
    billing.trialExpiration = new Date(trialExpiration);
    billing.groupBilling = settings.groupBilling;
    billing.legacyRestrictions = settings.legacyRestrictions;

    billing.subscription = this.getSubscription(settings);
    billing.payment = this.getBillingPayment(settings);

    billing.licenses = {
      dashpivotContributors: settings.contributorsLicenses,
      sitematePaidUsers: settings.sitematePaidUsersLicenses,
    };

    billing.rates = {
      dashpivotContributorsLicensesRate: settings.contributorsLicensesRate,
      dashpivotVisitorsLicensesRate: settings.visitorsLicensesRate,
      sitemateFreeUsersLicensesRate: settings.sitemateFreeUsersLicensesRate,
      sitematePaidUsersLicensesRate: settings.sitematePaidUsersLicensesRate,
    };

    if (settings.billingCycle) {
      billing.billingCycle = settings.billingCycle;
    }

    return cloneDeep(billing);
  }

  private getSubscription(settings: ICompanyHomeSettings): IBillingSubscription {
    const subscription = {
      status: settings.subscriptionStatus,
      term: null,
      initialDate: null,
    };

    if (settings.subscriptionTerm) {
      subscription.term = settings.subscriptionTerm;
    }

    if (settings.subscriptionInitialDate?.year) {
      const subscriptionInitialDate = this.convertToUTC(settings.subscriptionInitialDate);

      subscription.initialDate = new Date(subscriptionInitialDate);
    }

    return subscription;
  }

  private getBillingPayment(settings: ICompanyHomeSettings): IBillingPayment {
    return {
      termDay: settings.paymentTermDay ?? null,
      termContext: settings.paymentTermContext ?? null,
      method: settings.paymentMethod ?? null,
      currency: settings.paymentCurrency ?? null,
    };
  }

  convertToNgbDate(isoDate: string | Date): NgbDate {
    const date = new Date(isoDate);
    return new NgbDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
  }

  convertToUTC(ngbDate: NgbDate): number {
    return Date.UTC(ngbDate.year, ngbDate.month - 1, ngbDate.day);
  }

  convertToISODate(ngbDate: NgbDate): string {
    return new Date(this.convertToUTC(ngbDate)).toISOString();
  }
}
